import Utils from '@/modules/casino/utils/Utils';
import {
  initiateBridgerCustomWalletDeposit,
  updateBridgerCustomWalletDeposit,
} from '@/modules/casino/store/actions/deposit';
import { BRIDGER_CUSTOM_WALLET_LOCAL_STORAGE_KEY } from '@/constants/paymentProvider';
import ExpiringLocalStorage from '@/modules/casino/utils/ExpiringLocalStorage';
import i18n from 'i18next';

const CUSTOM_WALLET_PLACEHOLDER = 'bridger-custom-wallet-placeholder';

declare global {
  interface Window {
    BP_GOOGLE_PAY_WALLET_BUTTON_CONFIG: Record<string, string | number>;
    BRIDGERPAY_CONFIG: {
      hideWallets: string[];
    };
  }
}
/**
 * Bridger Apple Pay class
 * @deprecated
 */
class BridgerCustomWallet {
  private amount: any;
  private bonusId: any;
  private rewardId: any;
  private triggerSuccess: any;
  private triggerError: any;
  private dispatch: any;
  private cashierKey: any;
  private cashierToken: any;
  private isAvailable: {
    googlePay: boolean;
    applePay: boolean;
  } = {
    googlePay: false,
    applePay: false,
  };

  constructor(data: any) {
    window.BP_GOOGLE_PAY_WALLET_BUTTON_CONFIG = this.getGoogleStyleConfig();
    const hideWallets :string[]= [];
    if (window.config.depositSettings.providers?.[7]?.isApplePayActive !== '1') {
      hideWallets.push('ApplePay');
    }
    if (window.config.depositSettings.providers?.[7]?.isGooglePayActive !== '1') {
      hideWallets.push('GooglePay');
    }

    window.BRIDGERPAY_CONFIG = {
      hideWallets: hideWallets
    };

    this.init(data);

  }

  startSession() {
    const LS_KEY = 'google_pay_session_started_semaphor';
    if(ExpiringLocalStorage.get(LS_KEY)) {
      return;
    }

    ExpiringLocalStorage.set(LS_KEY,'1',1);
    if (!this.isCustomWalletAvailable()) return;
    if (this.amount > 0) {
      if (!(this.cashierKey && this.cashierToken)) {
        if(window.config.depositLogs === '1') {
          console.log('[DEPOSIT LOGS] GooglePay startSession',this.isCustomWalletAvailable(),this.amount,!(this.cashierKey && this.cashierToken));
        }
        this.dispatch(
          initiateBridgerCustomWalletDeposit({
            amount: this.amount * 100,
            bonusId: this.bonusId || null,
            rewardId: this.rewardId || null,
          }),
        );
      }
    }
  }

  updateSession() {
    if (window.config.depositLogs) {
      console.log('[DEPOSIT LOGS] Google Pay updateSession',this.isCustomWalletAvailable(),this.amount);
    }
    if (!this.isCustomWalletAvailable()) return;
    if (this.amount > 0) {
      this.dispatch(
        updateBridgerCustomWalletDeposit({
          amount: this.amount * 100,
          bonusId: this.bonusId || null,
          rewardId: this.rewardId || null,
          cashierKey: this.cashierKey || null,
        }),
      );
    }
  }

  injectCustomWalletScript(data: any): void {
    this.destroy();
    if(window.config.depositLogs === '1') {
      console.log('[DEPOSIT LOGS] Google Pay start injectCustomWalletScript',data);
    }
    // this.injectButton();
    Utils.injectScript(
      'https://ng-checkout.bridgerpay.com/v2/launcher',
      'google-pay',
      () => {
        if(window.config.depositLogs === '1') {
          console.log('[DEPOSIT LOGS] Google Pay start injectCustomWalletScript - done',data);
        }
        const { cashierKey, cashierToken} = data;
        this.cashierKey = cashierKey;
        this.cashierToken = cashierToken;
        if(window.config.depositLogs === '1') {
          console.log('[DEPOSIT LOGS] Google Pay script injected', cashierKey, cashierToken);
        }
        //listen for message
        window.addEventListener(
          `[bp][checkout:${this.cashierKey}]:wallet-result`,
          (e: any) => {
            if(window.config.depositLogs === '1') {
              console.log('[DEPOSIT LOGS] Google Pay wallet-result', e);
            }
            const result = e.detail;
            if (result.status === 'approved') {
              this.triggerSuccess();
            } else {
              this.triggerError({error: e});
            }
            ExpiringLocalStorage.remove(BRIDGER_CUSTOM_WALLET_LOCAL_STORAGE_KEY);
          },
          false,
        );
        window.addEventListener(
          `[bp][checkout:${this.cashierKey}]:wallet-cancelled`,
          (e:any) => {
            if(window.config.depositLogs === '1') {
              console.log('[DEPOSIT LOGS] Google Pay wallet-canceled', e);
            }
            // this.triggerError({error: e});
            ExpiringLocalStorage.remove(BRIDGER_CUSTOM_WALLET_LOCAL_STORAGE_KEY);
          },
          false,
        );
        window.addEventListener(
          `[bp][checkout:${this.cashierKey}]:no-wallet-provider-found`,
          (e:any) => {
            if(window.config.depositLogs === '1') {
              console.log('[DEPOSIT LOGS] Google Pay no-wallet-provider-found', e);
            }
            this.triggerError({error: e.type, soft: true});
            ExpiringLocalStorage.remove(BRIDGER_CUSTOM_WALLET_LOCAL_STORAGE_KEY);
          },
          false,
        );
        window.addEventListener(
          `[bp][checkout:${this.cashierKey}]:wallet-provider-cannot-be-used`,
          (e:any) => {
            if(window.config.depositLogs === '1') {
              console.log('[DEPOSIT LOGS] Google Pay wallet-provider-cannot-be-used', e);
            }
            if (e.details.wallet_type !== 'GooglePay' && e.details.wallet_type !== 'ApplePay') {
              this.triggerError({error:e, soft: true});
              ExpiringLocalStorage.remove(BRIDGER_CUSTOM_WALLET_LOCAL_STORAGE_KEY);
            }
          },
          false,
        );
        window.addEventListener(
          `[bp][checkout:${this.cashierKey}]:wallet-shipping-contact-update`,
          (e: any) => {
            if(window.config.depositLogs === '1') {
              console.log('[DEPOSIT LOGS] Google Pay wallet-shihpping-contact-update', e);
            }
            if (e.error || e.detail?.error) {
              this.triggerError({error: e.error ?? e.detail?.error});
              ExpiringLocalStorage.remove(BRIDGER_CUSTOM_WALLET_LOCAL_STORAGE_KEY);
            }
          },
          false,
        );
        window.addEventListener(
          `[bp][checkout:${this.cashierKey}]:wallet-button-is-ready`,
          this.buttonIsReady.bind(this),
          false,
        );

      },
      {
        data: {
          'cashier-key': data.cashierKey,
          'cashier-token': data.cashierToken,
          'button-mode': 'wallet',
        },
        container: CUSTOM_WALLET_PLACEHOLDER,
      },
    );
  }

  buttonIsReady(e:any): void {
    const {wallet_type} = e.detail;
    if(window.config.depositLogs === '1') {
      console.log(`[DEPOSIT LOGS] ${wallet_type} wallet-button-is-ready`, e);
    }

    if(wallet_type === 'GooglePay') {
      this.isAvailable.googlePay = true;
      this.addGoogleStyles();
    } else if (wallet_type === 'ApplePay') {
      this.isAvailable.applePay = true;
      this.addAppleStyles();
    }
  }
  addGoogleStyles(): void {
    const googlePayButton = document.querySelector('.wallet-google-pay') as HTMLButtonElement;
    const height = this.getStylePropertyValue('--btn-lg-height','48px');

    googlePayButton.style.setProperty('height',height, 'important');
  }

  getStylePropertyValue(property: string, defaultValue: string): string {
    const style = window.getComputedStyle(document.body);
    const value = style.getPropertyValue(property);
    if(!value) {
      return defaultValue;
    }
    return value;
  }
  addAppleStyles(): void {
    const applePayButtonContainer = document.querySelector('.wallet-apple-pay') as HTMLButtonElement;
    let applePayButton = applePayButtonContainer.querySelector('.wallet-apple-pay apple-pay-button') as HTMLButtonElement;
    const lang = i18n.language || window.config.defaultLanguage || 'en';
    const buttonColor = this.getButtonColor('apple');

    const height = this.getStylePropertyValue('--btn-lg-height','48px');

    const radius = this.getStylePropertyValue('--btn-lg-radius','8px');

    if (applePayButton) {

      applePayButtonContainer.style.setProperty('-webkit-appearance','-apple-pay-button', 'important');
      applePayButtonContainer.style.setProperty('--apple-pay-button-height',height, 'important');
      applePayButtonContainer.style.setProperty('height',height, 'important');
      applePayButtonContainer.style.setProperty('--apple-pay-button-border-radius',radius, 'important');

      applePayButtonContainer.style.setProperty('--apple-pay-button-type', 'plain', 'important');
      applePayButton.setAttribute('type', 'plain');
      applePayButtonContainer.style.setProperty('--apple-pay-button-style', buttonColor, 'important');
      applePayButton.setAttribute('buttonStyle', buttonColor);

      applePayButton.setAttribute('lang', lang);
    } else {
      applePayButton = applePayButtonContainer.querySelector('button') as HTMLButtonElement;
      applePayButton.style.setProperty('-webkit-appearance','-apple-pay-button', 'important');
      applePayButton.style.setProperty('height',height, 'important');
      applePayButton.style.setProperty('--apple-pay-button-border-radius',radius, 'important');
      applePayButton.style.setProperty('-apple-pay-button-type', 'plain', 'important');
      applePayButton.style.setProperty('-apple-pay-button-style', buttonColor, 'important');
      applePayButton.setAttribute('lang', lang);
    }
  }

  getButtonColor(type: 'google' | 'apple'): 'white' | 'black' {
    // @ts-ignore
    return _store.getState().templatesConfig.themeMode === 'dark' ? (type === 'google' ? 'white' : 'white-outline') : (type ==='apple' ? 'white-outline' : 'black');
  }

  getGoogleStyleConfig() {
    const radius = this.getStylePropertyValue('--btn-lg-radius','8px');
    // calculate radius number based on calc( 8px * 2.5) or just px value
    let radiusNumber = 8;
    if (radius) {
      if (radius.includes('calc')) {
        const match = radius.match(/calc\(\s*([\d.]+)\s*px\s*\*\s*([\d.]+)\s*\)/);
        if (match && match.length === 3) {
          const val1 = parseFloat(match[1]);
          const val2 = parseFloat(match[2]);
          radiusNumber = val1 * val2;
        }
      } else if (radius.includes('px')) {
        radiusNumber = parseFloat(radius.replace('px', ''));
      }
    }

    console.log('[DEPOSIT LOGS] Google Pay button radius',radiusNumber);


    return {
      buttonColor: this.getButtonColor('google'),
      buttonType: 'plain',
      buttonRadius: radiusNumber,
      buttonBorderType: 'default_border',
      buttonSizeMode: 'fill',
    };
  }

  destroy(): void {
    // remove google pay script and apple pay button
    const googlePayButton = document.getElementById(CUSTOM_WALLET_PLACEHOLDER);
    if (googlePayButton) {
      googlePayButton.innerHTML = '';
    }
  }

  init(data: any): void {
    this.triggerSuccess = data.triggerSuccess;
    this.triggerError = (e:any) => {
      if(window.config?.depositLogs === '1') {
        console.log('[DEPOSIT LOGS] google bridger tigger error', e);
      }
      data.triggerError(e);
    };
    this.dispatch = data.dispatch;
    this.cashierKey = data.cashierKey;
    this.cashierToken = data.cashierToken;
    this.isAvailable.googlePay = this.isAvailable.googlePay ?? false;
    this.isAvailable.applePay = this.isAvailable.applePay ?? false;

  }

  setAmount(amount: number): void {
    this.amount = amount ?? this.amount;
  }

  setBonusId(bonusId: number | null): void {
    this.bonusId = bonusId;
  }

  setRewardId(rewardId: number | string | null): void {
    this.rewardId = rewardId;
  }

  setPaymentDetails(data: any): void {
    const oldBonus = this.bonusId && !isNaN(this.bonusId) ? parseInt(this.bonusId, 10) : 0;
    const newBonus = data.bonusId && !isNaN(data.bonusId) ? parseInt(data.bonusId, 10) : 0;
    const oldReward = this.rewardId && !isNaN(this.rewardId) ? parseInt(this.rewardId, 10) : 0;
    const newReward = data.rewardId && !isNaN(data.rewardId) ? parseInt(data.rewardId, 10) : 0;

    if (this.amount !== data.amount || oldBonus !== newBonus || oldReward !== newReward) {
      this.setAmount(data.amount ?? this.amount);
      this.setBonusId(data.bonusId);
      this.setRewardId(data.rewardId);
      this.updateSession();
    }
  }

  isApplePayAvailable() {
    return this.isAvailable.applePay ?? false;
  }

  isGooglePayAvailable() {

    console.log('[DEPOSIT LOGS] Google Pay available',this.isAvailable);

    return this.isAvailable.googlePay ?? false;
  }

  isCustomWalletAvailable() {
    if(window.config.depositSettings.providers?.[7]) {
      return window.config.depositSettings.providers[7].isGooglePayActive === '1' || window.config.depositSettings.providers[7].isApplePayActive === '1';
    }
    return false;

  }

  onClick = () => {};

}

export default BridgerCustomWallet;
